<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell *ngIf="loadError">
    <p role="alert">{{ loadError }}</p>
    <p-button label="Back to Vehicles" icon="pi pi-arrow-left" routerLink="/fleet/vehicles"></p-button>
</app-page-shell>

<app-page-shell *ngIf="!loadError">
    <app-page-hero
        eyebrow="Fleet Vehicles"
        [title]="formTitle"
        [subtitle]="vehicle.vehicleId ? 'Update vehicle identity, specifications, documents, and compliance metadata.' : 'Create a vehicle record with identity, registration, specifications, and compliance metadata.'">
        <div actions>
            <p-button *ngIf="embedded" label="Cancel" icon="fa-solid fa-xmark" severity="secondary" [outlined]="true" (onClick)="cancelEmbedded()"></p-button>
            <p-button label="Clear" icon="fa-solid fa-xmark" severity="secondary" [outlined]="true" (onClick)="clearForm()"></p-button>
            <p-button [label]="vehicle.vehicleId ? 'Save Changes' : 'Create Vehicle'" icon="fa-solid fa-floppy-disk"
                [loading]="vehicleCreationInProgress" (onClick)="saveVehicle()" [disabled]="vehicleCreationInProgress"></p-button>
        </div>
    </app-page-hero>

    <form class="overFlow" [formGroup]="createEditVehicleForm" novalidate autocomplete="off" autocorrect="off"
        autocapitalize="off" spellcheck="off">
        <div class="p-fluid fleet-form-stack">
            <app-page-form-section icon="fa-solid fa-truck" title="Basic Information" subtitle="Plate, registration, and engine identifiers.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-4" [class.validation-field-invalid]="isFieldInvalid('plateNumberInput')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-hashtag"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="plateNumber" type="text" pInputText [(ngModel)]="vehicle.plateNumber" formControlName="plateNumberInput" />
                                <label for="plateNumber">Plate Number</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('plateNumberInput')) {
                        <small class="p-error field-error">{{ getFieldError('plateNumberInput') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-4" [class.validation-field-invalid]="isFieldInvalid('registrationNumberInput')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-file-signature"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="registrationNumber" type="text" pInputText [(ngModel)]="vehicle.registrationNumber" formControlName="registrationNumberInput" />
                                <label for="registrationNumber">Registration Number</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('registrationNumberInput')) {
                        <small class="p-error field-error">{{ getFieldError('registrationNumberInput') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-4" [class.validation-field-invalid]="isFieldInvalid('engineNumberInput')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-gears"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="engineNumber" type="text" pInputText [(ngModel)]="vehicle.engineNumber" formControlName="engineNumberInput" />
                                <label for="engineNumber">Engine Number</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('engineNumberInput')) {
                        <small class="p-error field-error">{{ getFieldError('engineNumberInput') }}</small>
                        }
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section icon="fa-solid fa-globe" title="Registration Jurisdiction" subtitle="Country, city, authority, and issuance metadata.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-4" [class.validation-field-invalid]="isFieldInvalid('registrationCountryDropdown')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-flag"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="registrationCountry" [options]="countries" [(ngModel)]="selectedRegistrationCountry" optionLabel="name"
                                    (onChange)="onRegistrationCountryChange($event)" [filter]="true" filterBy="name,countryCode"
                                    [showClear]="true" formControlName="registrationCountryDropdown" styleClass="w-full"></p-select>
                                <label for="registrationCountry">Registration Country</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('registrationCountryDropdown')) {
                        <small class="p-error field-error">{{ getFieldError('registrationCountryDropdown') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-4" [class.validation-field-invalid]="isFieldInvalid('registrationCityDropdown')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-city"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="registrationCity" [options]="registrationCities" [(ngModel)]="selectedRegistrationCity" optionLabel="cityName"
                                    (onChange)="onRegistrationCityChange($event)" [filter]="true" filterBy="cityName" [showClear]="true"
                                    formControlName="registrationCityDropdown" styleClass="w-full"></p-select>
                                <label for="registrationCity">Registration City</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('registrationCityDropdown')) {
                        <small class="p-error field-error">{{ getFieldError('registrationCityDropdown') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-building-shield"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="registrationAuthority" type="text" pInputText [(ngModel)]="vehicle.registrationAuthority" [ngModelOptions]="{standalone: true}" />
                                <label for="registrationAuthority">Registration Authority</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-calendar-plus"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-datePicker id="registrationIssueDate" [(ngModel)]="selectedRegistrationIssueDate" dateFormat="dd/mm/yy" formControlName="registrationIssueDateInput" styleClass="w-full"></p-datePicker>
                                <label for="registrationIssueDate">Registration Issue Date</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4" [class.validation-field-invalid]="isFieldInvalid('registrationDateInput')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-calendar-day"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-datePicker id="registrationDate" [(ngModel)]="selectedRegistrationDate" dateFormat="dd/mm/yy" formControlName="registrationDateInput" styleClass="w-full"></p-datePicker>
                                <label for="registrationDate">Registration Expiry</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('registrationDateInput')) {
                        <small class="p-error field-error">{{ getFieldError('registrationDateInput') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-briefcase"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="registrationUsage" [options]="registrationUsageOptions" [(ngModel)]="vehicle.registrationUsage" [ngModelOptions]="{standalone: true}"
                                    optionLabel="label" optionValue="value" [showClear]="true" styleClass="w-full"></p-select>
                                <label for="registrationUsage">Registration Usage</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section icon="fa-solid fa-fingerprint" title="Identity" subtitle="Chassis, plate origin, and plate classification.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-3" [class.validation-field-invalid]="isFieldInvalid('chassisNumberInput')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-barcode"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="chassisNumber" type="text" pInputText [(ngModel)]="vehicle.chassisNumber" formControlName="chassisNumberInput" />
                                <label for="chassisNumber">Chassis / VIN</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('chassisNumberInput')) {
                        <small class="p-error field-error">{{ getFieldError('chassisNumberInput') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-3">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-location-dot"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="plateSource" [options]="plateSourceOptions" [(ngModel)]="vehicle.plateSource" [ngModelOptions]="{standalone: true}"
                                    optionLabel="label" optionValue="value" [showClear]="true" styleClass="w-full"></p-select>
                                <label for="plateSource">Plate Source</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-3">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-id-card"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="plateType" [options]="plateTypeOptions" [(ngModel)]="vehicle.plateType" [ngModelOptions]="{standalone: true}"
                                    optionLabel="label" optionValue="value" [showClear]="true" styleClass="w-full"></p-select>
                                <label for="plateType">Plate Type</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-3">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-palette"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="plateColor" [options]="plateColorOptions" [(ngModel)]="vehicle.plateColor" [ngModelOptions]="{standalone: true}"
                                    optionLabel="label" optionValue="value" [showClear]="true" styleClass="w-full"></p-select>
                                <label for="plateColor">Plate Color</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section icon="fa-solid fa-sliders" title="Specifications" subtitle="Vehicle type, configuration, capacity, make, model, and year.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-4" [class.validation-field-invalid]="isFieldInvalid('vehicleTypeDropdown')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-car"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="vehicleType" [options]="vehicleTypes" [(ngModel)]="selectedVehicleType" optionLabel="name"
                                    (onChange)="onVehicleTypeChange($event)" [filter]="true" filterBy="name" [showClear]="true"
                                    formControlName="vehicleTypeDropdown" styleClass="w-full"></p-select>
                                <label for="vehicleType">Vehicle Type</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('vehicleTypeDropdown')) {
                        <small class="p-error field-error">{{ getFieldError('vehicleTypeDropdown') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-4" [class.validation-field-invalid]="isFieldInvalid('vehicleConfigDropdown')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-cogs"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="vehicleConfig" [options]="vehicleConfigs" [(ngModel)]="selectedVehicleConfig" optionLabel="name"
                                    (onChange)="onVehicleConfigChange($event)" [filter]="true" filterBy="name" [showClear]="true"
                                    formControlName="vehicleConfigDropdown" styleClass="w-full"></p-select>
                                <label for="vehicleConfig">Configuration</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('vehicleConfigDropdown')) {
                        <small class="p-error field-error">{{ getFieldError('vehicleConfigDropdown') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-4" [class.validation-field-invalid]="isFieldInvalid('vehicleCapacityDropdown')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-weight-hanging"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="vehicleCapacity" [options]="vehicleCapacities" [(ngModel)]="selectedVehicleCapacity" optionLabel="name"
                                    (onChange)="onVehicleCapacityChange($event)" [filter]="true" filterBy="name" [showClear]="true"
                                    formControlName="vehicleCapacityDropdown" styleClass="w-full"></p-select>
                                <label for="vehicleCapacity">Capacity</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('vehicleCapacityDropdown')) {
                        <small class="p-error field-error">{{ getFieldError('vehicleCapacityDropdown') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-4" [class.validation-field-invalid]="isFieldInvalid('vehicleMakeDropdown')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-car-side"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="vehicleMake" [options]="vehicleMakes" [(ngModel)]="selectedVehicleMake" optionLabel="name"
                                    (onChange)="onVehicleMakeChange($event)" [filter]="true" filterBy="name" [showClear]="true"
                                    formControlName="vehicleMakeDropdown" styleClass="w-full"></p-select>
                                <label for="vehicleMake">Vehicle Make</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('vehicleMakeDropdown')) {
                        <small class="p-error field-error">{{ getFieldError('vehicleMakeDropdown') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-4" [class.validation-field-invalid]="isFieldInvalid('vehicleModelDropdown')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-car-rear"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="vehicleModel" [options]="vehicleModels" [(ngModel)]="selectedVehicleModel" optionLabel="name"
                                    (onChange)="onVehicleModelChange($event)" [filter]="true" filterBy="name" [showClear]="true"
                                    formControlName="vehicleModelDropdown" styleClass="w-full"></p-select>
                                <label for="vehicleModel">Vehicle Model</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('vehicleModelDropdown')) {
                        <small class="p-error field-error">{{ getFieldError('vehicleModelDropdown') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-4" [class.validation-field-invalid]="isFieldInvalid('vehicleYearDropdown')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-calendar"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="vehicleYear" [options]="vehicleYears" [(ngModel)]="selectedVehicleYear" optionLabel="year"
                                    (onChange)="onVehicleYearChange($event)" [filter]="true" filterBy="year" [showClear]="true"
                                    formControlName="vehicleYearDropdown" styleClass="w-full"></p-select>
                                <label for="vehicleYear">Vehicle Year</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('vehicleYearDropdown')) {
                        <small class="p-error field-error">{{ getFieldError('vehicleYearDropdown') }}</small>
                        }
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section icon="fa-solid fa-user-shield" title="Ownership" subtitle="Registered owner and ownership classification.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-briefcase"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="ownershipType" [options]="ownershipTypeOptions" [(ngModel)]="vehicle.ownershipType" [ngModelOptions]="{standalone: true}"
                                    optionLabel="label" optionValue="value" [showClear]="true" styleClass="w-full"></p-select>
                                <label for="ownershipType">Ownership Type</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-user"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="registeredOwnerName" type="text" pInputText [(ngModel)]="vehicle.registeredOwnerName" [ngModelOptions]="{standalone: true}" />
                                <label for="registeredOwnerName">Registered Owner Name</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-id-badge"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="registeredOwnerIdentityNumber" type="text" pInputText [(ngModel)]="vehicle.registeredOwnerIdentityNumber" [ngModelOptions]="{standalone: true}" />
                                <label for="registeredOwnerIdentityNumber">Owner Identity / CR Number</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section icon="fa-solid fa-route" title="Capabilities" subtitle="Payload and trip types this vehicle can support.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-6" [class.validation-field-invalid]="isFieldInvalid('payloadTypesMultiSelect')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-box"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-multiSelect id="payloadTypes" [options]="payloadTypes" [(ngModel)]="selectedPayloadTypes" optionLabel="name" formControlName="payloadTypesMultiSelect" styleClass="w-full"></p-multiSelect>
                                <label for="payloadTypes">Payload Types</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('payloadTypesMultiSelect')) {
                        <small class="p-error field-error">{{ getFieldError('payloadTypesMultiSelect') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-6" [class.validation-field-invalid]="isFieldInvalid('tripTypesMultiSelect')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-road"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-multiSelect id="tripTypes" [options]="tripTypes" [(ngModel)]="selectedTripTypes" optionLabel="name" formControlName="tripTypesMultiSelect" styleClass="w-full"></p-multiSelect>
                                <label for="tripTypes">Trip Types</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('tripTypesMultiSelect')) {
                        <small class="p-error field-error">{{ getFieldError('tripTypesMultiSelect') }}</small>
                        }
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section icon="fa-solid fa-shield-halved" title="Insurance" subtitle="Insurance company, policy, and supporting document.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-3 flex align-items-center gap-2 pt-4">
                        <p-checkbox inputId="isInsured" [(ngModel)]="vehicle.isInsured" [ngModelOptions]="{standalone: true}" [binary]="true"></p-checkbox>
                        <label for="isInsured">Vehicle is insured</label>
                    </div>
                    <div class="field col-12 md:col-3">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-building"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="insuranceCompany" [options]="insuranceCompanies" [(ngModel)]="selectedInsuranceCompany"
                                    (onChange)="onInsuranceCompanyChange($event)" optionLabel="name" [showClear]="true"
                                    formControlName="insuranceCompanyDropdown" styleClass="w-full"></p-select>
                                <label for="insuranceCompany">Insurance Company</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-3">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-file-contract"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="insurancePolicyNo" type="text" pInputText [(ngModel)]="vehicle.insurancePolicyNo" [ngModelOptions]="{standalone: true}" />
                                <label for="insurancePolicyNo">Insurance Policy Number</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-3">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-money-bill-wave"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="insuranceAmount" type="number" pInputText [(ngModel)]="vehicle.insuranceAmount" [ngModelOptions]="{standalone: true}" />
                                <label for="insuranceAmount">Insurance Amount</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-calendar-plus"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-datePicker id="insuranceIssueDate" [(ngModel)]="selectedInsuranceIssueDate" dateFormat="dd/mm/yy" formControlName="insuranceIssueDateInput" styleClass="w-full"></p-datePicker>
                                <label for="insuranceIssueDate">Insurance Issue Date</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-calendar-day"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-datePicker id="insuranceExpiryDate" [(ngModel)]="selectedInsuranceExpiryDate" dateFormat="dd/mm/yy" formControlName="insuranceExpiryDateInput" styleClass="w-full"></p-datePicker>
                                <label for="insuranceExpiryDate">Insurance Expiry</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-fileUpload name="insuranceFile" accept=".pdf,.png,.jpg" mode="basic" [customUpload]="true"
                            (onSelect)="onInsuranceDocumentUpload($event)" chooseLabel="Upload Insurance Document"
                            chooseIcon="fa-solid fa-shield-halved" styleClass="w-full"></p-fileUpload>
                        @if (vehicle.insuranceDocumentUrl) {
                        <a [href]="vehicle.insuranceDocumentUrl" target="_blank" class="document-link">Insurance document uploaded</a>
                        }
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section icon="fa-solid fa-clipboard-check" title="Compliance" subtitle="Technical inspection and operating card metadata.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-screwdriver-wrench"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="technicalInspectionNumber" type="text" pInputText [(ngModel)]="vehicle.technicalInspectionNumber" [ngModelOptions]="{standalone: true}" />
                                <label for="technicalInspectionNumber">Technical Inspection / Fahas Number</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-calendar-plus"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-datePicker id="technicalInspectionIssueDate" [(ngModel)]="selectedTechnicalInspectionIssueDate" dateFormat="dd/mm/yy" formControlName="technicalInspectionIssueDateInput" styleClass="w-full"></p-datePicker>
                                <label for="technicalInspectionIssueDate">Inspection Issue Date</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-calendar-day"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-datePicker id="technicalInspectionExpiryDate" [(ngModel)]="selectedTechnicalInspectionExpiryDate" dateFormat="dd/mm/yy" formControlName="technicalInspectionExpiryDateInput" styleClass="w-full"></p-datePicker>
                                <label for="technicalInspectionExpiryDate">Inspection Expiry Date</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-fileUpload name="technicalInspectionFile" accept=".pdf,.png,.jpg" mode="basic" [customUpload]="true"
                            (onSelect)="onTechnicalInspectionDocumentUpload($event)" chooseLabel="Upload Inspection Document"
                            chooseIcon="fa-solid fa-clipboard-check" styleClass="w-full"></p-fileUpload>
                        @if (vehicle.technicalInspectionDocumentUrl) {
                        <a [href]="vehicle.technicalInspectionDocumentUrl" target="_blank" class="document-link">Inspection document uploaded</a>
                        }
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-id-card-clip"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="operatingCardNumber" type="text" pInputText [(ngModel)]="vehicle.operatingCardNumber" [ngModelOptions]="{standalone: true}" />
                                <label for="operatingCardNumber">Operating Card Number</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-flag"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="operatingCardCountry" [options]="countries" [(ngModel)]="selectedOperatingCardCountry" [ngModelOptions]="{standalone: true}"
                                    (onChange)="onOperatingCardCountryChange($event)" optionLabel="name" [filter]="true" filterBy="name,countryCode"
                                    [showClear]="true" styleClass="w-full"></p-select>
                                <label for="operatingCardCountry">Operating Card Country</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-road-bridge"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="operatingCardActivity" type="text" pInputText [(ngModel)]="vehicle.operatingCardActivity" [ngModelOptions]="{standalone: true}" />
                                <label for="operatingCardActivity">Operating Card Activity</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-calendar-plus"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-datePicker id="operatingCardIssueDate" [(ngModel)]="selectedOperatingCardIssueDate" dateFormat="dd/mm/yy" formControlName="operatingCardIssueDateInput" styleClass="w-full"></p-datePicker>
                                <label for="operatingCardIssueDate">Operating Card Issue Date</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-calendar-day"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-datePicker id="operatingCardExpiryDate" [(ngModel)]="selectedOperatingCardExpiryDate" dateFormat="dd/mm/yy" formControlName="operatingCardExpiryDateInput" styleClass="w-full"></p-datePicker>
                                <label for="operatingCardExpiryDate">Operating Card Expiry Date</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-signal"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="operatingCardStatus" [options]="operatingCardStatusOptions" [(ngModel)]="vehicle.operatingCardStatus" [ngModelOptions]="{standalone: true}"
                                    optionLabel="label" optionValue="value" [showClear]="true" styleClass="w-full"></p-select>
                                <label for="operatingCardStatus">Operating Card Status</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-fileUpload name="operatingCardFile" accept=".pdf,.png,.jpg" mode="basic" [customUpload]="true"
                            (onSelect)="onOperatingCardDocumentUpload($event)" chooseLabel="Upload Operating Card"
                            chooseIcon="fa-solid fa-id-card-clip" styleClass="w-full"></p-fileUpload>
                        @if (vehicle.operatingCardDocumentUrl) {
                        <a [href]="vehicle.operatingCardDocumentUrl" target="_blank" class="document-link">Operating card uploaded</a>
                        }
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section icon="fa-solid fa-file-lines" title="Registration Document" subtitle="Registration card upload.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-5">
                        <p-fileUpload name="registrationCardFile" accept=".pdf,.png,.jpg" mode="basic" [customUpload]="true"
                            (onSelect)="onRegistrationCardUpload($event)" chooseLabel="Upload Registration Card"
                            chooseIcon="fa-solid fa-id-card" styleClass="w-full"></p-fileUpload>
                    </div>
                    <div class="field col-12 md:col-7 flex align-items-center">
                        @if (vehicle.registrationImage) {
                        <a [href]="vehicle.registrationImage" target="_blank" class="document-link">Registration document uploaded</a>
                        } @else {
                        <span class="text-600">No registration document uploaded.</span>
                        }
                    </div>
                </div>
            </app-page-form-section>

            @if (isRental || isFreelancer) {
            <app-page-form-section icon="fa-solid fa-building-user" title="External Ownership" subtitle="Link this vehicle to the exact transporter CRM client when it belongs to a third-party transporter.">
                <div class="formgrid grid">
                    <div class="field col-12">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-truck-ramp-box"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="vehicleTransporterClient" [options]="transporterClients" [(ngModel)]="selectedTransporterClient"
                                    optionLabel="companyName" (onChange)="onTransporterClientChange($event)" [filter]="true"
                                    filterBy="companyName,clientNumber,linkedTransportationAgencyName" [showClear]="true"
                                    [ngModelOptions]="{standalone: true}" placeholder="Optional" styleClass="w-full"></p-select>
                                <label for="vehicleTransporterClient">Transporter CRM Client</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                </div>
                @if (isRental) {
                <div class="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3 mt-3 p-3 surface-50 border-1 border-200 border-round">
                    <span class="text-700">Rental agreement, rate cards, and settlement terms are managed centrally in Fleet &gt; Rental &gt; Agreements.</span>
                    <p-button label="Open Rental Agreements" icon="pi pi-external-link" severity="secondary" [outlined]="true"
                        [routerLink]="['/fleet/rentals/agreements']"></p-button>
                </div>
                }
            </app-page-form-section>
            }
        </div>
    </form>
</app-page-shell>
